// /pages/carrossier/FormulaireB/index.js
import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import FormulaireHeader from "./components/FormulaireHeader";
import SectionVerificateur from "./components/SectionVerificateur";
import SectionClient from "./components/SectionClient";
import SectionVehicule from "./components/SectionVehicule";
import SectionPorte from "./components/SectionPorte";
import SectionControles from "./components/SectionControles";
import SectionCleverSafe from "./components/SectionCleverSafe";
import SectionMesures from "./components/SectionMesures";
import SectionDeclaration from "./components/SectionDeclaration";
import { useFormData } from "./hooks/useFormData";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { validateForm } from "./utils/validation";

const FormulaireTypeB = ({ onComplete, produit, index }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Récupération des paramètres depuis l'URL
  const { orderId, produitId } = useParams();
  const [searchParams] = useSearchParams();
  const unit = searchParams.get('unit');
  
  const productId = produitId;
  
  // Debug avec useEffect
  useEffect(() => {
    console.log("🔍 FormulaireTypeB HUSKY monté avec:", { 
      productId, 
      orderId, 
      produitName: produit?.name,
      index,
      unit
    });
  }, [productId, orderId, produit?.name, index, unit]);
  
  // Hooks personnalisés
  const { formData, handleChange, handleControleChange } = useFormData();
  const { handleSubmit } = useFormSubmit({
    productId,
    orderId,
    produit,
    index,
    formData,
    onComplete,
    setLoading,
    setErrors
  });

  // Validation du formulaire
  const onSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      alert("⚠️ Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setErrors({});
    await handleSubmit(e);
  };

  // Vérification que orderId est disponible
  if (!orderId) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">❌ Erreur de navigation</h3>
          <p className="text-red-700 mb-4">
            L'ID du dossier est manquant dans l'URL. Veuillez retourner au dashboard et cliquer à nouveau sur le formulaire.
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            ← Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        <FormulaireHeader produit={produit} />
        
        <form onSubmit={onSubmit} className="space-y-8">
          <SectionVerificateur 
            formData={formData} 
            handleChange={handleChange} 
            errors={errors}
          />
          
          <SectionClient 
            formData={formData} 
            handleChange={handleChange} 
            errors={errors}
          />
          
          <SectionVehicule 
            formData={formData} 
            handleChange={handleChange} 
            errors={errors}
          />
          
          <SectionPorte 
            formData={formData} 
            handleChange={handleChange} 
            errors={errors}
            produit={produit}
          />
          
          <SectionControles 
            formData={formData} 
            handleControleChange={handleControleChange}
            productId={productId}
          />
          
          <SectionCleverSafe 
            formData={formData} 
            handleControleChange={handleControleChange}
            productId={productId}
          />
          
          <SectionMesures 
            formData={formData} 
            handleControleChange={handleControleChange}
            productId={productId}
          />
          
          <SectionDeclaration 
            formData={formData}
            setFormData={(updater) => {
              if (typeof updater === 'function') {
                const newData = updater(formData);
                Object.keys(newData).forEach(section => {
                  if (section === 'declarationHonneur') {
                    handleChange('declarationHonneur', '', newData[section]);
                  } else {
                    Object.keys(newData[section]).forEach(field => {
                      handleChange(section, field, newData[section][field]);
                    });
                  }
                });
              }
            }}
            errors={errors}
            produit={produit}
            loading={loading}
          />
        </form>
      </div>
    );
  } catch (error) {
    console.error("❌ Erreur de rendu dans FormulaireTypeB:", error);
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">❌ Erreur d'affichage du formulaire</h3>
          <p className="text-red-700 mb-4">
            Une erreur s'est produite lors du chargement du formulaire TypeB HUSKY.
          </p>
          <div className="bg-red-100 p-3 rounded text-sm text-red-800">
            <strong>Détails de l'erreur :</strong>
            <pre>{error.message}</pre>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            🔄 Recharger la page
          </button>
        </div>
      </div>
    );
  }
};

export default FormulaireTypeB;